import React from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardDocumentCheckIcon,
  ChatBubbleLeftRightIcon,
  TruckIcon,
  ClockIcon,
  MapPinIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import Header from './app/Header';
import Footer from './app/Footer';
import FooterCopyright from './app/FooterCopyright';
import { SHARED } from './utils/product';

const steps = [
  {
    number: '01',
    icon: ClipboardDocumentCheckIcon,
    title: 'Assina o plano',
    desc: 'Escolha Diário, Semanal ou Mensal — a partir de R$ 290. Confirmamos cobertura na Grande Goiânia e ativamos a assinatura.',
    detail: 'Diário: coleta em dias úteis. Semanal: um dia fixo por semana. Mensal: uma janela agendada no mês, encaixada num dia de rota.',
  },
  {
    number: '02',
    icon: ChatBubbleLeftRightIcon,
    title: 'Solicita as coletas',
    desc: 'Peça coletas pelo app ou WhatsApp, com origem, destino e descrição do pacote.',
    detail: 'Apenas sacolas e pacotes: até 40×40×40 cm e 8 kg. Sem móveis, eletrodomésticos ou mudanças.',
  },
  {
    number: '03',
    icon: TruckIcon,
    title: 'O carro Fretz passa na rota',
    desc: 'A frota identificada faz circuitos compartilhados. Seu pedido entra no dia de rota do plano escolhido.',
    detail: 'Várias empresas usam o mesmo veículo. É isso que barateia o plano em relação a um carro próprio ocioso.',
  },
  {
    number: '04',
    icon: ClockIcon,
    title: 'Encontre o veículo na porta',
    desc: 'O carro estaciona nas proximidades. Alguém precisa encontrar o veículo em até 15 minutos.',
    detail: 'Se ninguém aparecer, a rota continua. A espera limitada é a regra que permite o preço baixo.',
  },
  {
    number: '05',
    icon: MapPinIcon,
    title: 'Entrega no destino',
    desc: 'Diário: até 36 horas após a coleta (cobre coletas no fim do dia). Semanal e Mensal: até 72 horas úteis após a coleta.',
    detail: 'O destinatário também encontra o veículo na porta, com a mesma regra de 15 minutos.',
  },
];

const ComoFunciona = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 bg-mesh-light" aria-hidden="true" />
        <div className="relative container mx-auto px-4 lg:px-6 py-16 lg:py-24">
          <div className="max-w-2xl">
            <span className="badge-brand mb-5">Rotas Fretz</span>
            <h1 className="font-display text-display-xl text-ink-900">
              Como funciona
            </h1>
            <p className="mt-5 text-xl text-ink-500 leading-relaxed">
              Assina, solicita a coleta, o carro Fretz passa na rota, você encontra
              o veículo em até {SHARED.doorWait} e a encomenda chega no SLA do plano.
            </p>
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container mx-auto px-4 lg:px-6 max-w-4xl">
          <div className="relative">
            <div className="absolute left-6 lg:left-8 top-0 bottom-0 w-px bg-ink-100 hidden sm:block" aria-hidden="true" />

            <div className="space-y-8">
              {steps.map(({ number, icon: Icon, title, desc, detail }, i) => (
                <div key={number} className="relative sm:pl-20 lg:pl-24">
                  <div className="absolute left-0 top-0 hidden sm:flex flex-col items-center gap-2">
                    <div className="h-12 w-12 lg:h-16 lg:w-16 rounded-2xl bg-brand-gradient text-white flex items-center justify-center shadow-glow z-10">
                      <Icon className="h-6 w-6 lg:h-7 lg:w-7" />
                    </div>
                  </div>

                  <div className="card p-6 lg:p-8 hover:-translate-y-0.5 transition-transform">
                    <div className="flex items-start gap-4">
                      <div className="sm:hidden h-10 w-10 rounded-xl bg-brand-gradient text-white flex items-center justify-center shadow-glow shrink-0">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-baseline gap-3 mb-2">
                          <span className="font-display text-4xl font-extrabold text-brand-100">{number}</span>
                          <h2 className="font-display text-xl font-bold text-ink-900">{title}</h2>
                        </div>
                        <p className="text-ink-700 font-medium">{desc}</p>
                        <p className="mt-2 text-ink-500 text-sm">{detail}</p>
                      </div>
                    </div>
                  </div>

                  {i < steps.length - 1 && (
                    <div className="flex justify-center sm:justify-start sm:pl-5 my-3 text-ink-300">
                      <ArrowRightIcon className="h-4 w-4 rotate-90" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-ink-50">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="rounded-4xl bg-brand-gradient p-10 lg:p-14 text-center text-white shadow-glow relative overflow-hidden">
            <div className="absolute inset-0 bg-mesh-dark opacity-40" aria-hidden="true" />
            <div className="relative">
              <h2 className="font-display text-display-lg text-white">Pronto para a rota?</h2>
              <p className="mt-3 text-white/80 text-lg max-w-xl mx-auto">
                Planos a partir de {SHARED.fromPrice}/mês. Sem simulador de picape.
              </p>
              <div className="mt-8 flex flex-wrap gap-4 justify-center">
                <Link to="/#contato" className="btn-accent">Quero Fretz Empresas</Link>
                <Link to="/precos" className="btn-dark !bg-white/10 hover:!bg-white/20 text-white border-white/20 border">
                  Ver planos
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FooterCopyright />
    </div>
  );
};

export default ComoFunciona;
